import { ReactNode } from 'react';
import { useRouter } from 'next/router';
import { briefIsShowable, useMorningBriefQuery } from '../../../lib/brief';
import { BriefState, isBriefPulseCreditPaused, isBriefPulsePaused, resolveBriefState } from '../../../lib/brief-state';
import { usePulses } from '../../../lib/pulses';
import { useWorkspaces } from '../../../lib/workspaces';
import { useNarrow } from '../../../lib/use-narrow';
import { Card } from '../ui';
import { Home2Brief, MaestroBrief, MobileMaestroBrief } from '../MaestroBrief';
import { ActivateBrief } from './ActivateBrief';
import { MobileActivateBrief } from './MobileActivateBrief';
import { MobileNewWorkspaceBrief } from './MobileNewWorkspaceBrief';
import { MobileUpgradeBrief } from './MobileUpgradeBrief';
import { NewWorkspaceBrief } from './NewWorkspaceBrief';
import { UpgradeBrief } from './UpgradeBrief';

// The catch-up header's morning-brief slot — one of three states, picked by `resolveBriefState`
// (the native apps' decision, ported in lib/brief-state). Figma "Catch Up states" 7419:47452.
//
// The redesign puts the desktop brief inside a white DS Card at ~⅔ width beside the QR (the "card with
// buttons" — see `DesktopHomeHeader`). The mobile variants are already floating cards of their own
// (`MobileMaestroBrief` etc.), so on narrow the state renders bare.

// `home2` (symphonyHome2Web) swaps the active-brief (`hero`) state for the redesigned `Home2Brief` card;
// every other state is untouched. The other surfaces keep their current brief.
// Dev/preview override (default-off): `?briefState=<state>` forces the slot to a static offer card so
// the design can be reviewed without a real credit-paused / user-paused account. A normal load carries
// no such param and resolves from real pulses (Golden Rule 8 — the native `SYMPHONY_BRIEF_STATE` twin).
// Restricted to the data-free states — `hero`/`newWorkspace` need real content, so they aren't forceable.
const PREVIEWABLE_BRIEF_STATES: readonly BriefState[] = ['activate', 'upgrade', 'none'];

export function BriefSlot({ home2 = false }: { home2?: boolean } = {}) {
  const { brief, settled: briefSettled } = useMorningBriefQuery();
  const { state: pulses } = usePulses();
  const workspaces = useWorkspaces();
  const narrow = useNarrow();
  const router = useRouter();

  const previewState = router.query.briefState;
  const forcedState =
    typeof previewState === 'string' && (PREVIEWABLE_BRIEF_STATES as readonly string[]).includes(previewState)
      ? (previewState as BriefState)
      : undefined;

  const resolvedState = resolveBriefState({
    pulsesLoaded: pulses.kind === 'ready',
    // Credit-paused wins over the user's own pause (resuming won't help without credits). No further
    // gate here: web's upgrade destination is always its own /packages picker — the plan-selector /
    // storefront gating the natives apply to this input is about THEIR destinations (Wixel, App Review
    // 3.1.1), neither of which exists on web.
    pulseCreditPaused: pulses.kind === 'ready' && isBriefPulseCreditPaused(pulses.pulses),
    pulsePaused: pulses.kind === 'ready' && isBriefPulsePaused(pulses.pulses),
    briefSettled,
    // A brief is "there" for the hero if it's showable — a finished brief with content, OR one still in
    // motion (created today, convening). A failed or stale brief is NOT, so the slot falls back to its
    // other states rather than to a prior day's brief.
    hasBrief: briefIsShowable(brief),
    hasWorkspace: workspaces.status === 'ready' && workspaces.workspaces.length > 0,
    // Until the workspaces fetch settles we can't tell newWorkspace from none for a never-seeded
    // routine, so hold `loading` rather than flashing nothing then the card.
    workspacesSettled: workspaces.status === 'ready',
  });

  const state = forcedState ?? resolvedState;

  const firstWorkspace = workspaces.status === 'ready' ? workspaces.workspaces[0] : undefined;

  let content: ReactNode = null;
  switch (state) {
    case 'hero':
      // Home 2 (desktop): the redesigned active-brief card. Mobile Home 2 keeps today's hero for now
      // (a mobile design lands later), so only the wide surface swaps.
      content = narrow ? <MobileMaestroBrief /> : home2 ? <Home2Brief /> : <MaestroBrief />;
      break;
    case 'newWorkspace':
      content = narrow ? (
        <MobileNewWorkspaceBrief workspace={firstWorkspace} />
      ) : (
        <NewWorkspaceBrief workspace={firstWorkspace} />
      );
      break;
    case 'activate':
      content = narrow ? <MobileActivateBrief /> : <ActivateBrief />;
      break;
    case 'upgrade':
      content = narrow ? <MobileUpgradeBrief /> : <UpgradeBrief />;
      break;
    default:
      return null;
  }

  if (narrow) return <>{content}</>;
  return <Card>{content}</Card>;
}
